<script setup lang="ts">
import { defineEmits, defineProps, ref, RenderFunction } from 'vue'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import { i18n } from '../../localization'

import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'

import BaseInputWrapper from './BaseInputWrapper.vue'
import CameraCapture from './CameraCapture.vue'

// Create component
const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize, FilePondPluginImagePreview)

const props = defineProps<{
    label: string
    name: string
    icon?: RenderFunction
    uploadedFile: any
    required?: boolean
    required2?: boolean
    rules?: any
    camera?: boolean
    errorMessage: string
    process: (
        fieldName,
        file,
        metadata,
        load,
        error,
        progress,
        abort,
        transfer,
        options
    ) => Promise<{ abort: () => void }>
}>()

const emit = defineEmits(['flush', 'update:modelValue'])

const inputSource = ref(false)

const pond = ref(null)

function handleInputSource(value) {
    if (value === false) {
        emit('flush')
    }
    inputSource.value = value
}

function handleFilePondInit() {
    // console.log('FilePond has initialized')
    // FilePond instance methods are available on `this.$refs.pond`
}

function handleModelValueUpdate(value) {
    emit('update:modelValue', value)
}
</script>

<template>
    <BaseInputWrapper
        v-if="!inputSource || inputSource === 'filepond'"
        :name="props.name"
        :label="props.label"
        :icon="icon"
        :required="required"
        :required2="required2">
        <!--      to deactivate instant upload:   :instant-upload="false"-->
        <file-pond
            ref="pond"
            name="test"
            class="-mb-2"
            :class="{ 'error': !!errorMessage }"
            :label-idle="i18n.global.t('verification.upload_label')"
            :label-invalid-field="i18n.global.t('verification.upload_invalid_field')"
            :label-file-waiting-for-size="i18n.global.t('verification.upload_waiting_for_size')"
            :label-file-size-not-available="i18n.global.t('verification.upload_size_not_available')"
            :label-file-loading="i18n.global.t('verification.upload_loading')"
            :label-file-load-error="i18n.global.t('verification.upload_load_error')"
            :label-file-processing="i18n.global.t('verification.upload_processing')"
            :label-file-processing-complete="i18n.global.t('verification.upload_processing_complete')"
            :label-file-processing-aborted="i18n.global.t('verification.upload_processing_aborted')"
            :label-file-processing-error="i18n.global.t('verification.upload_processing_error')"
            :label-file-processing-revert-error="i18n.global.t('verification.upload_processing_revert_error')"
            :label-file-remove-error="i18n.global.t('verification.upload_file_remove_error')"
            :label-tap-to-cancel="i18n.global.t('verification.upload_tap_to_cancel')"
            :label-tap-to-retry="i18n.global.t('verification.upload_tap_to_retry')"
            :label-tap-to-undo="i18n.global.t('verification.upload_tap_to_undo')"
            :label-button-remove-item="i18n.global.t('verification.upload_button_remove_item')"
            :label-button-abort-item-load="i18n.global.t('verification.upload_button_abort_item_load')"
            :label-button-retry-item-load="i18n.global.t('verification.upload_button_retry_item_load')"
            :label-button-abort-item-processing="i18n.global.t('verification.upload_button_abort_item_processing')"
            :label-button-undo-item-processing="i18n.global.t('verification.upload_button_undo_item_processing')"
            :label-button-retry-item-processing="i18n.global.t('verification.upload_button_retry_item_processing')"
            :label-button-process-item="i18n.global.t('verification.upload_button_process_item')"
            accepted-file-types="image/jpg, image/jpeg, image/png, application/pdf"
            :server="{ process }"
            :credits="false"
            :allow-multiple="false"
            v-bind="$attrs"
            @removefile="emit('flush')"
            @init="handleFilePondInit" />
    </BaseInputWrapper>
    <div v-if="(camera && !inputSource) || inputSource === 'filepond'" class="separator text-sm text-gray-400">
        {{ i18n.global.t('camera.or') }}
    </div>
    <CameraCapture
        v-if="camera && inputSource !== 'filepond'"
        @update:modelValue="handleModelValueUpdate"
        @update:inputSource="handleInputSource" />
</template>

<style scoped>
.separator {
    display: flex;
    align-items: center;
    text-align: center;
    margin-bottom: 0.6em;
}

.separator::before,
.separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #d1d5db;
}

.separator:not(:empty)::before {
    margin-right: 0.25em;
}

.separator:not(:empty)::after {
    margin-left: 0.25em;
}
</style>
